/**
 * Revenue Margin Cards
 *
 * Cards showing tokens/second ratio per operation.
 * ratio > 1.0 = green (profitable), ratio < 1.0 = red (loss).
 * Sorted worst margin first.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { OperationTimeStats } from '@/services/metricsAPI';

interface RevenueMarginCardProps {
  stats: OperationTimeStats[];
}

function getMarginColor(ratio: number): string {
  if (ratio >= 1.2) return 'text-green-400 bg-green-500/10 border-green-500/20';
  if (ratio >= 0.9) return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
  return 'text-red-400 bg-red-500/10 border-red-500/20';
}

function getMarginLabel(ratio: number): string {
  if (ratio >= 1.2) return '+';
  if (ratio >= 0.9) return '~';
  return '-';
}

export function RevenueMarginCard({ stats }: RevenueMarginCardProps): JSX.Element {
  const { t } = useTranslation();

  const filtered = stats
    .filter((s) => s.sample_count >= 3 && s.avg_processing_seconds > 0)
    .map((s) => ({ ...s, ratio: s.tokens_vs_seconds_ratio }))
    .sort((a, b) => a.ratio - b.ratio) // worst first
    .slice(0, 8);

  if (filtered.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 text-white/40 text-sm">
        {t('admin.metrics.noChartData')}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {filtered.map((s) => (
        <div
          key={`${s.operation}-${s.provider ?? 'all'}`}
          className={`rounded-lg border p-3 ${getMarginColor(s.ratio)}`}
        >
          <div className="text-xs font-medium truncate opacity-80">
            {s.operation.replace(/_/g, ' ')}
          </div>
          {s.provider && <div className="text-xs opacity-50 truncate">{s.provider}</div>}
          <div className="text-lg font-bold mt-1">
            {getMarginLabel(s.ratio)}
            {s.ratio.toFixed(2)}
          </div>
          <div className="text-xs opacity-60">tok/s</div>
        </div>
      ))}
    </div>
  );
}
